import { addMonths } from '@if/core/date'
import { EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { ComputeAmountForFrequencyDto, MonthlyPayment, useComputePaymentAmountForFrequency } from '@src/api/compute-api'
import { FilteredCreditApplication } from '@src/api/credit-api'
import { Constants, EMPTY_ARRAY } from '@src/types'
import { useMemo } from 'react'

export interface TermPaymentOption {
  firstPaymentOn?: string | null
  activationDate?: Date | null
  requestedAmount?: number | null
  paymentPlanId?: string | null
  includeInsurance?: boolean
}

const useTermPaymentsTable = (
  creditApp: FilteredCreditApplication | null,
  option?: TermPaymentOption,
): [MonthlyPayment[], boolean] => {
  const include84Months = (option?.requestedAmount ?? 0) >= Constants.LargeLoanAmount

  const dto = useMemo<ComputeAmountForFrequencyDto | null>(() => {
    if (creditApp) {
      return {
        merchantId: creditApp.merchantId,
        paymentPlanId: option?.paymentPlanId ?? creditApp.merchantPaymentPlanId ?? EPaymentPlan.RegularDailyInterests,
        interestRate: creditApp.finalDecision.interestRate,
        hasCoapplicant: creditApp.coapplicant !== null,
        stateIso: creditApp.applicant.province,
        paymentFrequency: EPaymentFrequency.Monthly,
        deliveryOn: formatDate(option?.activationDate ?? new Date()),
        firstPaymentOn: option?.firstPaymentOn ?? formatDate(addMonths(new Date(), 1)),
        maxPmtAmount: creditApp.finalDecision.maxPmtAmount,
        amountRequested:
          option?.requestedAmount ?? Math.min(creditApp.requestedLoanAmount, creditApp.finalDecision.maxAmountFinanced),
        includeInsurance: option?.includeInsurance ?? false,
        include84Months,
      }
    }
    return null
  }, [option, creditApp, include84Months])
  const [result, isLoading] = useComputePaymentAmountForFrequency(dto)
  if (!include84Months && result?.monthlyTerms)
    result.monthlyTerms = result.monthlyTerms.filter(
      (ta) => ta.term <= (creditApp?.finalDecision?.maxTermDuration ?? 0),
    )
  return [result?.monthlyTerms ?? EMPTY_ARRAY, isLoading]
}

export default useTermPaymentsTable
